'use client';

/**
 * Configuration dialog for one CRM opportunity tool.
 *
 * **Config-only**: this dialog edits CRM-specific whitelist settings
 * (allowed fields, allowed stages).
 * Operator-authored tool instructions are NOT edited here — they
 * follow the standard tool-instruction path used by every other tool
 * (Shopify, calendar, etc.):
 *
 *   `ai_tools.system_instructions` (default)
 *      → `tenant_ai_tool_instructions` (tenant default)
 *      → `tenant_ai_tool_instructions` (scoped per-agent override)
 *      → `applyStoredInstructions()` at runtime
 *
 * The shared `<ToolInstructionsDialog>` component edits that path.
 * This dialog provides a "Configure AI Instructions" button in the
 * footer that calls back to the parent (CrmToolsGroup → file-level
 * state) to open the shared instructions dialog.
 *
 * One dialog component serves both CRM tools via a `toolName`
 * discriminator. The body switches between `CrmFieldMultiselect`
 * (update-fields) and `CrmStageMultiselect` (move-stage) because
 * their value shapes diverge; the scaffolding (Dialog, header, Save
 * button, block-reason Alert, footer) is shared.
 */

import { AlertTriangle, Loader2, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  useCrmToolsConfig,
  type CrmToolBlockReason,
} from '@/hooks/agents/use-crm-tools-config';
import type { CrmOpportunityToolName, CrmTerminology } from '@/lib/ai/tools/crm-opportunity/client-types';
import { CRM_OPPORTUNITY_TOOL_NAME } from '@/lib/ai/tools/crm-opportunity/client-types';
import { CrmFieldMultiselect } from './crm-field-multiselect';
import { CrmStageMultiselect } from './crm-stage-multiselect';

interface CrmToolConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cfg: ReturnType<typeof useCrmToolsConfig>;
  toolName: CrmOpportunityToolName;
  canManage: boolean;
  /**
   * Open the shared `<ToolInstructionsDialog>` for this tool. Wired
   * by the parent (CrmToolsGroup) which receives state from the file-
   * level mounts in `auto-reply-settings.tsx` / `reply-settings.tsx`.
   */
  onOpenInstructions: () => void;
}

export function CrmToolConfigDialog({
  open,
  onOpenChange,
  cfg,
  toolName,
  canManage,
  onOpenInstructions,
}: CrmToolConfigDialogProps) {
  const isUpdateFields = toolName === CRM_OPPORTUNITY_TOOL_NAME.updateFields;

  if (cfg.status.kind !== 'ok') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure CRM tool</DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center text-sm text-muted-foreground">
            CRM schema is still loading…
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const data = cfg.status.data;
  const t = data.terminology;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        {isUpdateFields ? (
          <UpdateFieldsBody
            cfg={cfg}
            canManage={canManage}
            terminology={t}
            onOpenInstructions={onOpenInstructions}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <MoveStageBody
            cfg={cfg}
            canManage={canManage}
            terminology={t}
            onOpenInstructions={onOpenInstructions}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// =====================================================
// Update fields body
// =====================================================

function UpdateFieldsBody({
  cfg,
  canManage,
  terminology: t,
  onOpenInstructions,
  onClose,
}: {
  cfg: ReturnType<typeof useCrmToolsConfig>;
  canManage: boolean;
  terminology: CrmTerminology;
  onOpenInstructions: () => void;
  onClose: () => void;
}) {
  if (cfg.status.kind !== 'ok') return null;
  const draft = cfg.drafts[CRM_OPPORTUNITY_TOOL_NAME.updateFields];
  const data = cfg.status.data;
  const storedStale =
    data.settings[CRM_OPPORTUNITY_TOOL_NAME.updateFields]?.stale_field_codes ?? [];
  const disabled = !canManage || cfg.savingTool !== null;
  const saveDisabled =
    disabled || !cfg.isUpdateFieldsDirty || cfg.updateFieldsBlockReason !== null;

  return (
    <>
      <DialogHeader>
        <DialogTitle><span>Configure: Update</span> {t.opportunity.toLowerCase()} <span>fields</span></DialogTitle>
        <DialogDescription>
          <span>Whitelist the CRM fields this agent is allowed to write on the
          current</span> {t.opportunity.toLowerCase()}<span>. The agent never sees fields outside this list.</span>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Allowed fields</Label>
          <CrmFieldMultiselect
            schema={data.fieldSchema}
            systemFields={data.systemFieldSchema}
            value={draft.allowed_field_codes}
            staleCodes={storedStale}
            onChange={(next) =>
              cfg.setUpdateFieldsDraft({ allowed_field_codes: next })
            }
            disabled={disabled}
          />
        </div>

        <BlockReasonAlert reason={cfg.updateFieldsBlockReason} />
      </div>

      <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={onOpenInstructions}
        >
          <Settings className="mr-1.5 h-3 w-3" />
          <span>Configure AI Instructions</span>
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={cfg.savingTool !== null}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={async () => {
              const ok = await cfg.saveUpdateFields();
              if (ok) onClose();
            }}
            disabled={saveDisabled}
          >
            {cfg.savingTool === CRM_OPPORTUNITY_TOOL_NAME.updateFields && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            <span>Save</span>
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

// =====================================================
// Move stage body
// =====================================================

function MoveStageBody({
  cfg,
  canManage,
  terminology: t,
  onOpenInstructions,
  onClose,
}: {
  cfg: ReturnType<typeof useCrmToolsConfig>;
  canManage: boolean;
  terminology: CrmTerminology;
  onOpenInstructions: () => void;
  onClose: () => void;
}) {
  if (cfg.status.kind !== 'ok') return null;
  const draft = cfg.drafts[CRM_OPPORTUNITY_TOOL_NAME.moveStage];
  const data = cfg.status.data;
  const stored = data.settings[CRM_OPPORTUNITY_TOOL_NAME.moveStage];
  const staleStages = stored?.stale_stage_codes ?? [];
  const disabled = !canManage || cfg.savingTool !== null;
  const saveDisabled =
    disabled || !cfg.isMoveStageDirty || cfg.moveStageBlockReason !== null;

  return (
    <>
      <DialogHeader>
        <DialogTitle><span>Configure: Move</span> {t.opportunity.toLowerCase()} {t.stage.toLowerCase()}</DialogTitle>
        <DialogDescription>
          <span>Select which</span> {t.pipeline.toLowerCase()} {t.stagePlural.toLowerCase()} <span>the agent is
          allowed to move</span> {t.opportunityPlural.toLowerCase()} <span>into.</span>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <CrmStageMultiselect
          pipelines={data.pipelines}
          stageValue={draft.allowed_stage_codes}
          staleStageCodes={staleStages}
          onStageChange={(next) =>
            cfg.setMoveStageDraft({ allowed_stage_codes: next })
          }
          terminology={t}
          disabled={disabled}
        />

        <BlockReasonAlert reason={cfg.moveStageBlockReason} />
      </div>

      <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={onOpenInstructions}
        >
          <Settings className="mr-1.5 h-3 w-3" />
          <span>Configure AI Instructions</span>
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={cfg.savingTool !== null}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={async () => {
              const ok = await cfg.saveMoveStage();
              if (ok) onClose();
            }}
            disabled={saveDisabled}
          >
            {cfg.savingTool === CRM_OPPORTUNITY_TOOL_NAME.moveStage && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            <span>Save</span>
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

// =====================================================
// Shared block-reason alert
// =====================================================

function BlockReasonAlert({ reason }: { reason: CrmToolBlockReason }) {
  if (reason === null) return null;
  return (
    <Alert>
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        Select at least one live entry before saving. To turn the tool off
        instead, use the switch on the tile.
      </AlertDescription>
    </Alert>
  );
}
